import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Button,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import {
  VolumeUp as VolumeUpIcon,
  Mic as MicIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import { getKokoroTTSService } from '../../services/voice/kokoroSingleton';

/**
 * All 28 Kokoro voice IDs with human-readable labels.
 * Grouped by accent and gender for easy scanning.
 */
const KOKORO_VOICES = [
  // American female
  { id: 'af_heart', label: 'Heart -- American female (default)' },
  { id: 'af_alloy', label: 'Alloy -- American female' },
  { id: 'af_aoede', label: 'Aoede -- American female' },
  { id: 'af_bella', label: 'Bella -- American female' },
  { id: 'af_jessica', label: 'Jessica -- American female' },
  { id: 'af_kore', label: 'Kore -- American female' },
  { id: 'af_nicole', label: 'Nicole -- American female' },
  { id: 'af_nova', label: 'Nova -- American female' },
  { id: 'af_river', label: 'River -- American female' },
  { id: 'af_sarah', label: 'Sarah -- American female' },
  { id: 'af_sky', label: 'Sky -- American female' },
  // American male
  { id: 'am_adam', label: 'Adam -- American male' },
  { id: 'am_echo', label: 'Echo -- American male' },
  { id: 'am_eric', label: 'Eric -- American male' },
  { id: 'am_fenrir', label: 'Fenrir -- American male' },
  { id: 'am_liam', label: 'Liam -- American male' },
  { id: 'am_michael', label: 'Michael -- American male' },
  { id: 'am_onyx', label: 'Onyx -- American male' },
  { id: 'am_orion', label: 'Orion -- American male' },
  { id: 'am_puck', label: 'Puck -- American male' },
  // British female
  { id: 'bf_alice', label: 'Alice -- British female' },
  { id: 'bf_emma', label: 'Emma -- British female' },
  { id: 'bf_isabella', label: 'Isabella -- British female' },
  { id: 'bf_lily', label: 'Lily -- British female' },
  // British male
  { id: 'bm_daniel', label: 'Daniel -- British male' },
  { id: 'bm_fable', label: 'Fable -- British male' },
  { id: 'bm_george', label: 'George -- British male' },
  { id: 'bm_lewis', label: 'Lewis -- British male' },
];

// eslint-disable-next-line max-lines-per-function
const VoiceSettings = () => {
  const { settings, updateSettingsCategory } = useUserPreferences();
  const [voiceSettings, setVoiceSettings] = useState(settings?.voice || {});
  const [saving, setSaving] = useState(false);
  const [testingVoice, setTestingVoice] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Model download progress state:
  // null = unknown (initial), 'loading' = indeterminate progress,
  // { progress: number } = determinate progress (0-100), 'ready' = done
  const [modelStatus, setModelStatus] = useState(null);

  // Update local state when settings change
  useEffect(() => {
    if (settings?.voice) {
      setVoiceSettings(settings.voice);
    }
  }, [settings]);

  // Model download: handle all three cases on mount
  useEffect(() => {
    const kokoro = getKokoroTTSService();

    if (kokoro.isReady()) {
      // Model already loaded (fast path — no progress bar needed)
      setModelStatus('ready');
      return;
    }

    if (kokoro.isModelLoading()) {
      // Model already loading (e.g. FaanaVoiceService started it)
      // Do NOT call init() again — it won't update the progress callback
      // (singleton init() is idempotent: returns same promise on re-entry)
      setModelStatus('loading');
      return;
    }

    // Not ready and not loading — start download now
    setModelStatus('loading');
    kokoro.init((data) => {
      if (data.status === 'progress' && data.progress != null) {
        setModelStatus({ progress: Math.round(data.progress) });
      } else if (data.status === 'ready') {
        setModelStatus('ready');
      }
    });
  }, []);

  const handleChange = (path, value) => {
    const keys = path.split('.');
    const updated = { ...voiceSettings };
    let current = updated;

    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = current[keys[i]] || {};
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    setVoiceSettings(updated);
    setHasChanges(true);
    setSaveMessage(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      await updateSettingsCategory('voice', voiceSettings);
      setHasChanges(false);
      setSaveMessage({ type: 'success', text: 'Voice settings saved successfully!' });
    } catch (error) {
      console.error('Failed to save voice settings:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const testVoice = () => {
    setTestingVoice(true);
    const kokoro = getKokoroTTSService();
    const voiceId = KOKORO_VOICES.some(v => v.id === voiceSettings.tts?.voice)
      ? voiceSettings.tts.voice
      : 'af_heart';
    kokoro.previewVoice(voiceId);
    // previewVoice is fire-and-forget; reset after ~3s sample duration
    setTimeout(() => setTestingVoice(false), 3000);
  };

  // Resolve selected voice — fall back to default if legacy browser voice stored
  const selectedVoiceId = KOKORO_VOICES.some(v => v.id === voiceSettings.tts?.voice)
    ? voiceSettings.tts.voice
    : 'af_heart';

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Voice & Audio Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure voice recognition and text-to-speech settings for the Faana AI ChatBot.
      </Typography>

      {/* Master Toggle */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={voiceSettings.enabled || false}
              onChange={(e) => handleChange('enabled', e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Enable Voice Features</Typography>
              <Typography variant="caption" color="text.secondary">
                Turn on voice recognition and text-to-speech capabilities
              </Typography>
            </Box>
          }
        />
      </Paper>

      {/* Voice Recognition Settings */}
      <Paper sx={{ p: 3, mb: 3, opacity: voiceSettings.enabled ? 1 : 0.5 }}>
        <Typography variant="h6" gutterBottom>
          <MicIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Voice Recognition
        </Typography>

        <Stack spacing={3}>
          <TextField
            label="Wake Word"
            value={voiceSettings.wakeWord || 'hey coach'}
            onChange={(e) => handleChange('wakeWord', e.target.value)}
            disabled={!voiceSettings.enabled}
            helperText="Say this phrase to activate voice input"
            fullWidth
          />

          <FormControl fullWidth disabled={!voiceSettings.enabled}>
            <InputLabel>Language</InputLabel>
            <Select
              value={voiceSettings.language || 'en-US'}
              onChange={(e) => handleChange('language', e.target.value)}
              label="Language"
            >
              <MenuItem value="en-US">English (US)</MenuItem>
              <MenuItem value="en-GB">English (UK)</MenuItem>
              <MenuItem value="es-ES">Spanish (Spain)</MenuItem>
              <MenuItem value="fr-FR">French (France)</MenuItem>
              <MenuItem value="de-DE">German (Germany)</MenuItem>
              <MenuItem value="it-IT">Italian (Italy)</MenuItem>
              <MenuItem value="pt-BR">Portuguese (Brazil)</MenuItem>
              <MenuItem value="zh-CN">Chinese (Mandarin)</MenuItem>
              <MenuItem value="ja-JP">Japanese</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Text-to-Speech Settings */}
      <Paper sx={{ p: 3, mb: 3, opacity: voiceSettings.enabled ? 1 : 0.5 }}>
        <Typography variant="h6" gutterBottom>
          <VolumeUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Text-to-Speech
        </Typography>

        <Stack spacing={3}>
          <FormControlLabel
            control={
              <Switch
                checked={voiceSettings.tts?.enabled !== false}
                onChange={(e) => handleChange('tts.enabled', e.target.checked)}
                disabled={!voiceSettings.enabled}
              />
            }
            label="Enable voice responses"
          />

          {/* Model download progress bar — shown while model is loading */}
          {modelStatus && modelStatus !== 'ready' && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {typeof modelStatus === 'object'
                  ? `Loading voice model... ${modelStatus.progress}%`
                  : 'Preparing voice model...'}
              </Typography>
              <LinearProgress
                variant={typeof modelStatus === 'object' ? 'determinate' : 'indeterminate'}
                value={typeof modelStatus === 'object' ? modelStatus.progress : undefined}
                sx={{ mt: 0.5, borderRadius: 1 }}
              />
            </Box>
          )}

          <FormControl fullWidth disabled={!voiceSettings.enabled || !voiceSettings.tts?.enabled}>
            <InputLabel>Voice</InputLabel>
            <Select
              value={selectedVoiceId}
              onChange={(e) => handleChange('tts.voice', e.target.value)}
              label="Voice"
            >
              {KOKORO_VOICES.map(v => (
                <MenuItem key={v.id} value={v.id}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography gutterBottom>
              Speed: {voiceSettings.tts?.speed || 1.0}x
            </Typography>
            <Slider
              value={voiceSettings.tts?.speed || 1.0}
              onChange={(e, value) => handleChange('tts.speed', value)}
              min={0.5}
              max={2}
              step={0.1}
              marks={[
                { value: 0.5, label: '0.5x' },
                { value: 1, label: '1x' },
                { value: 1.5, label: '1.5x' },
                { value: 2, label: '2x' },
              ]}
              disabled={!voiceSettings.enabled || !voiceSettings.tts?.enabled}
            />
          </Box>

          <Box>
            <Typography gutterBottom>
              Volume: {Math.round((voiceSettings.tts?.volume || 1.0) * 100)}%
            </Typography>
            <Slider
              value={voiceSettings.tts?.volume || 1.0}
              onChange={(e, value) => handleChange('tts.volume', value)}
              min={0}
              max={1}
              step={0.1}
              marks={[
                { value: 0, label: '0%' },
                { value: 0.5, label: '50%' },
                { value: 1, label: '100%' },
              ]}
              disabled={!voiceSettings.enabled || !voiceSettings.tts?.enabled}
            />
          </Box>

          <Button
            variant="outlined"
            startIcon={<VolumeUpIcon />}
            onClick={testVoice}
            disabled={!voiceSettings.enabled || !voiceSettings.tts?.enabled || testingVoice}
          >
            {testingVoice ? 'Playing...' : 'Test Voice'}
          </Button>
        </Stack>
      </Paper>

      {/* Behavior Settings */}
      <Paper sx={{ p: 3, mb: 3, opacity: voiceSettings.enabled ? 1 : 0.5 }}>
        <Typography variant="h6" gutterBottom>
          Behavior
        </Typography>

        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={voiceSettings.behavior?.accumulationMode !== false}
                onChange={(e) => handleChange('behavior.accumulationMode', e.target.checked)}
                disabled={!voiceSettings.enabled}
              />
            }
            label={
              <Box>
                <Typography>Accumulation Mode</Typography>
                <Typography variant="caption" color="text.secondary">
                  Review and edit text before sending (recommended)
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={voiceSettings.behavior?.feedbackSounds !== false}
                onChange={(e) => handleChange('behavior.feedbackSounds', e.target.checked)}
                disabled={!voiceSettings.enabled}
              />
            }
            label="Play feedback sounds"
          />

          <FormControlLabel
            control={
              <Switch
                checked={voiceSettings.behavior?.continuousListening !== false}
                onChange={(e) => handleChange('behavior.continuousListening', e.target.checked)}
                disabled={!voiceSettings.enabled}
              />
            }
            label="Keep listening after wake word"
          />

          <TextField
            type="number"
            label="Active duration (seconds)"
            value={voiceSettings.behavior?.bypassDuration || 15}
            onChange={(e) => handleChange('behavior.bypassDuration', parseInt(e.target.value))}
            disabled={!voiceSettings.enabled || !voiceSettings.behavior?.continuousListening}
            InputProps={{ inputProps: { min: 5, max: 60 } }}
            helperText="How long to stay active after wake word"
            sx={{ maxWidth: 200 }}
          />
        </Stack>
      </Paper>

      {/* Advanced Settings */}
      <Paper sx={{ p: 3, mb: 3, opacity: voiceSettings.enabled ? 1 : 0.5 }}>
        <Typography variant="h6" gutterBottom>
          Advanced
        </Typography>

        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={voiceSettings.advanced?.adaptiveBattery !== false}
                onChange={(e) => handleChange('advanced.adaptiveBattery', e.target.checked)}
                disabled={!voiceSettings.enabled}
              />
            }
            label="Adaptive battery optimization"
          />

          <FormControlLabel
            control={
              <Switch
                checked={voiceSettings.advanced?.noiseSupression !== false}
                onChange={(e) => handleChange('advanced.noiseSupression', e.target.checked)}
                disabled={!voiceSettings.enabled}
              />
            }
            label="Noise suppression"
          />

          <FormControlLabel
            control={
              <Switch
                checked={voiceSettings.advanced?.echoCancellation !== false}
                onChange={(e) => handleChange('advanced.echoCancellation', e.target.checked)}
                disabled={!voiceSettings.enabled}
              />
            }
            label="Echo cancellation"
          />
        </Stack>
      </Paper>

      {/* Save Message */}
      {saveMessage && (
        <Alert severity={saveMessage.type} sx={{ mb: 2 }} onClose={() => setSaveMessage(null)}>
          {saveMessage.text}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
        {hasChanges && (
          <Chip
            label="You have unsaved changes"
            color="warning"
            size="small"
            icon={<InfoIcon />}
          />
        )}
      </Box>
    </Box>
  );
};

export default VoiceSettings;
